import { useEffect, useMemo, useRef, useState } from "react";
import type {
  Id,
  NoteColumnDef,
  ScriptColumn,
  ScriptDocument,
} from "@supermanus/document-model";
import {
  getCueNote,
  getEpisodeScenes,
  getSceneCues,
  isCueSideContentEmpty,
} from "@supermanus/document-model";
import { cueContentPlainText } from "./cueRichText";
import { countPlainOccurrences } from "./findSelect";

export type FindScope = "scene" | "episode";
export type FindTarget = "script" | "notes" | "both";

export type FindMatch = {
  key: string;
  sceneId: Id;
  sceneNumber: string | number;
  cueId: Id;
  kind: "script" | "notes";
  column: ScriptColumn | Id;
  columnLabel: string;
  /** 0-based occurrence of the query within this cell. */
  occurrence: number;
  query: string;
};

export function findInDocument(opts: {
  document: ScriptDocument;
  episodeId: Id;
  sceneId: Id | null;
  scope: FindScope;
  target: FindTarget;
  query: string;
  noteColumns: NoteColumnDef[];
}): FindMatch[] {
  const q = opts.query.trim();
  if (!q) return [];

  const scenes =
    opts.scope === "scene" && opts.sceneId
      ? getEpisodeScenes(opts.document, opts.episodeId).filter(
          (s) => s.id === opts.sceneId,
        )
      : getEpisodeScenes(opts.document, opts.episodeId);

  const noteColumns = opts.noteColumns;
  const primaryNoteId = noteColumns[0]?.id ?? null;
  const matches: FindMatch[] = [];

  for (const scene of scenes) {
    const cues = getSceneCues(opts.document, scene.id);
    for (const cue of cues) {
      if (opts.target === "script" || opts.target === "both") {
        for (const column of ["visual", "audio"] as ScriptColumn[]) {
          const raw =
            column === "visual" ? cue.visual.content : cue.audio.content;
          if (isCueSideContentEmpty(raw)) continue;
          const plain = cueContentPlainText(raw);
          const count = countPlainOccurrences(plain, q);
          for (let occurrence = 0; occurrence < count; occurrence++) {
            matches.push({
              key: `${cue.id}:${column}:${occurrence}`,
              sceneId: scene.id,
              sceneNumber: scene.number,
              cueId: cue.id,
              kind: "script",
              column,
              columnLabel: column === "visual" ? "Billede" : "Lyd",
              occurrence,
              query: q,
            });
          }
        }
      }

      if (opts.target === "notes" || opts.target === "both") {
        const cols =
          noteColumns.length > 0
            ? noteColumns
            : cue.notes
              ? Object.keys(cue.notes).map((id, i) => ({
                  id,
                  title: "Noter",
                  sortOrder: i,
                }))
              : cue.sourceNote
                ? [{ id: "legacy", title: "Noter", sortOrder: 0 }]
                : [];
        for (const col of cols) {
          const raw =
            col.id === "legacy"
              ? (cue.sourceNote ?? "")
              : getCueNote(cue, col.id, primaryNoteId);
          if (isCueSideContentEmpty(raw)) continue;
          const plain = cueContentPlainText(raw);
          const count = countPlainOccurrences(plain, q);
          for (let occurrence = 0; occurrence < count; occurrence++) {
            matches.push({
              key: `${cue.id}:note:${col.id}:${occurrence}`,
              sceneId: scene.id,
              sceneNumber: scene.number,
              cueId: cue.id,
              kind: "notes",
              column: col.id,
              columnLabel: col.title.trim() || "Noter",
              occurrence,
              query: q,
            });
          }
        }
      }
    }
  }

  return matches;
}

export interface FindPanelProps {
  open: boolean;
  onClose: () => void;
  document: ScriptDocument;
  episodeId: Id;
  sceneId: Id | null;
  noteColumns: NoteColumnDef[];
  onJump: (match: FindMatch) => void;
}

export function FindPanel({
  open,
  onClose,
  document,
  episodeId,
  sceneId,
  noteColumns,
  onJump,
}: FindPanelProps) {
  /** Text currently in the input — does not trigger navigation. */
  const [draft, setDraft] = useState("");
  /** Query last submitted with Søg / Enter — drives matches and jumps. */
  const [committed, setCommitted] = useState("");
  const [scope, setScope] = useState<FindScope>("episode");
  const [target, setTarget] = useState<FindTarget>("both");
  const [activeIndex, setActiveIndex] = useState(0);
  const onJumpRef = useRef(onJump);
  onJumpRef.current = onJump;
  const lastJumpedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const matches = useMemo(
    () =>
      findInDocument({
        document,
        episodeId,
        sceneId,
        scope,
        target,
        query: committed,
        noteColumns,
      }),
    [document, episodeId, sceneId, scope, target, committed, noteColumns],
  );

  const matchSignature = `${committed}\0${scope}\0${target}\0${matches.length}\0${matches[0]?.key ?? ""}`;
  useEffect(() => {
    setActiveIndex(0);
    lastJumpedKeyRef.current = null;
  }, [matchSignature]);

  const safeIndex =
    matches.length === 0 ? 0 : Math.min(activeIndex, matches.length - 1);

  useEffect(() => {
    if (!open) return;
    if (!committed || matches.length === 0) return;
    const match = matches[safeIndex];
    if (!match) return;
    const jumpKey = `${match.key}@${safeIndex}`;
    if (lastJumpedKeyRef.current === jumpKey) return;
    lastJumpedKeyRef.current = jumpKey;
    onJumpRef.current(match);
  }, [open, committed, matches, safeIndex]);

  function runSearch(nextQuery?: string) {
    const q = (nextQuery ?? draft).trim();
    lastJumpedKeyRef.current = null;
    setActiveIndex(0);
    setCommitted(q);
    if (nextQuery !== undefined && nextQuery !== draft) {
      setDraft(nextQuery);
    }
  }

  function go(delta: number) {
    if (matches.length === 0) return;
    setActiveIndex((i) => {
      const cur = Math.min(i, matches.length - 1);
      return (cur + delta + matches.length) % matches.length;
    });
  }

  function onSubmitSearch() {
    const q = draft.trim();
    if (!q) {
      runSearch("");
      return;
    }
    // Same query already active → jump to next match (Enter = næste).
    if (q === committed && matches.length > 0) {
      go(1);
      return;
    }
    runSearch(q);
  }

  if (!open) return null;

  const hasCommitted = Boolean(committed);
  const countLabel = !hasCommitted
    ? ""
    : matches.length === 0
      ? "Ingen resultater"
      : `${safeIndex + 1} af ${matches.length}`;

  return (
    <div className="sm-find-panel sm-no-print" role="search">
      <div className="sm-find-row">
        <input
          type="search"
          className="sm-find-input"
          placeholder="Søg…"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (e.shiftKey) {
                if (committed && matches.length > 0) go(-1);
                return;
              }
              onSubmitSearch();
            } else if (e.key === "F3") {
              e.preventDefault();
              if (!committed || matches.length === 0) return;
              if (e.shiftKey) go(-1);
              else go(1);
            }
          }}
          aria-label="Søgetekst"
        />
        <button
          type="button"
          className="sm-find-submit"
          disabled={!draft.trim()}
          onClick={() => runSearch()}
        >
          Søg
        </button>
        <div className="sm-find-nav">
          <button
            type="button"
            disabled={!hasCommitted || matches.length === 0}
            title="Forrige (Shift+Enter)"
            aria-label="Forrige resultat"
            onClick={() => go(-1)}
          >
            Forrige
          </button>
          <button
            type="button"
            disabled={!hasCommitted || matches.length === 0}
            title="Næste (Enter når søgning er aktiv)"
            aria-label="Næste resultat"
            onClick={() => go(1)}
          >
            Næste
          </button>
        </div>
        <span className="sm-find-count" aria-live="polite">
          {countLabel}
        </span>
        <button type="button" onClick={onClose} aria-label="Luk søgning">
          Luk
        </button>
      </div>
      <div className="sm-find-row sm-find-options">
        <label>
          Omfang{" "}
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as FindScope)}
          >
            <option value="scene">Aktuel scene</option>
            <option value="episode">Hele episoden</option>
          </select>
        </label>
        <label>
          Mål{" "}
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value as FindTarget)}
          >
            <option value="both">Manus og noter</option>
            <option value="script">Kun manus</option>
            <option value="notes">Kun noter</option>
          </select>
        </label>
      </div>
    </div>
  );
}
